// Purpose: authenticated app shell. 240px Sidebar on the left, main
// column on the right with the session-expiry banner at the top and
// an <Outlet /> for the active page.
//
// Navigation is context-aware:
//   * Always: Shares, Dropboxes (workspace group).
//   * When user.role === 'ADMIN': add Admin group with Dashboard,
//     Approvals (with pending-count badge), Audit, Routing, Forensics,
//     Users, Settings.
//
// The Admin badge polls /api/admin/dashboard every 60s (see
// useAdminBadges hook).

import { useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { api } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { SessionExpiryBanner } from '../components/SessionExpiryBanner.tsx';
import {
  CheckSquareIcon,
  ClockIcon,
  FileTextIcon,
  GridIcon,
  SettingsIcon,
  UploadIcon,
  UsersIcon,
} from '../components/icons/index.tsx';
import { NoticeLink } from '../components/legal/NoticeLink.tsx';
import { Sidebar } from '../components/nav/Sidebar.tsx';
import type { SidebarSection } from '../components/nav/Sidebar.tsx';
import { UserMenu } from '../components/nav/UserMenu.tsx';

interface AdminBadges {
  pendingApprovals: number;
}

function useAdminBadges(enabled: boolean): AdminBadges {
  const [badges, setBadges] = useState<AdminBadges>({ pendingApprovals: 0 });

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get<{ pendingApprovals?: number }>('/admin/dashboard');
        if (!cancelled && typeof res.data.pendingApprovals === 'number') {
          setBadges({ pendingApprovals: res.data.pendingApprovals });
        }
      } catch {
        // Silent: badge just stays at its last value (or 0).
      }
    };
    void load();
    const id = window.setInterval(load, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [enabled]);

  return badges;
}

function PendingBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      className={[
        'inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full',
        'bg-status-warning-muted text-status-warning text-caption font-semibold',
      ].join(' ')}
      aria-label={`${count} pending`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function AppShell() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const { pendingApprovals } = useAdminBadges(isAdmin);

  const sections = useMemo<SidebarSection[]>(() => {
    const workspace: SidebarSection = {
      label: 'Workspace',
      items: [
        { to: '/shares', label: 'Shares', icon: <UploadIcon size={16} /> },
        { to: '/dropboxes', label: 'Dropboxes', icon: <FileTextIcon size={16} /> },
      ],
    };
    if (!isAdmin) return [workspace];
    const admin: SidebarSection = {
      label: 'Admin',
      items: [
        { to: '/admin', label: 'Dashboard', icon: <GridIcon size={16} />, end: true },
        {
          to: '/admin/approvals',
          label: 'Approvals',
          icon: <CheckSquareIcon size={16} />,
          badge: <PendingBadge count={pendingApprovals} />,
        },
        { to: '/admin/audit', label: 'Audit', icon: <ClockIcon size={16} /> },
        { to: '/admin/routing', label: 'Routing', icon: <FileTextIcon size={16} /> },
        { to: '/admin/forensics', label: 'Forensics', icon: <FileTextIcon size={16} /> },
        { to: '/admin/users', label: 'Users', icon: <UsersIcon size={16} /> },
        { to: '/admin/settings', label: 'Settings', icon: <SettingsIcon size={16} /> },
      ],
    };
    return [workspace, admin];
  }, [isAdmin, pendingApprovals]);

  return (
    <div className="min-h-screen flex bg-bg-primary text-fg-primary">
      <Sidebar
        sections={sections}
        footer={
          <div className="flex flex-col gap-3">
            <UserMenu />
            <NoticeLink className="px-2" />
          </div>
        }
      />
      <div className="flex-1 flex flex-col min-w-0">
        <SessionExpiryBanner />
        <main className="flex-1 overflow-x-hidden">
          <div className="mx-auto max-w-7xl px-8 py-10">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
